import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api";

export default function BerryFusionPanel({ stationId, onClose, refreshEquippedItems }) {
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [inventoryBerries, setInventoryBerries] = useState([]);
  const [itemMeta, setItemMeta] = useState({});
  const [recipes, setRecipes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [farmingLevel, setFarmingLevel] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);

  const [slot1, setSlot1] = useState(null);
  const [slot2, setSlot2] = useState(null);
  const [slot3, setSlot3] = useState(null);

  const [activeTab, setActiveTab] = useState("craft");

  useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const layout = useMemo(() => {
    const isMobile = viewport.width <= 768;
    const panelWidth = isMobile
      ? Math.max(320, viewport.width - 32)
      : Math.max(520, Math.min(viewport.width - 64, 860));

    const panelMaxHeight = Math.max(420, Math.min(Math.floor(viewport.height * 0.9), 820));

    return {
      isMobile,
      panelWidth,
      panelMaxHeight,
    };
  }, [viewport.width, viewport.height]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [stateRes, recipesRes, jobsRes] = await Promise.all([
        api.get("/v1/island/berry-fusion/state", { withCredentials: true }),
        api.get("/v1/island/berry-fusion/recipes", { withCredentials: true }),
        api.get("/v1/island/berry-fusion/jobs", { withCredentials: true }),
      ]);

      setInventoryBerries(stateRes.data?.berries || []);
      setItemMeta(stateRes.data?.item_meta || {});
      setRecipes(recipesRes.data?.recipes || []);
      setJobs(jobsRes.data?.jobs || []);
      setFarmingLevel(Number(stateRes.data?.farming_level || 1));
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load berry fusion.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    const interval = setInterval(() => {
      load();
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const selectedSlots = [slot1, slot2, slot3].filter(Boolean);

  const selectedCounts = useMemo(() => {
    const map = {};
    selectedSlots.forEach((b) => {
      const id = Number(b.item_id);
      map[id] = (map[id] || 0) + 1;
    });
    return map;
  }, [slot1, slot2, slot3]);

  const inventoryById = useMemo(() => {
    const map = {};
    (inventoryBerries || []).forEach((b) => {
      map[Number(b.item_id)] = b;
    });
    return map;
  }, [inventoryBerries]);

  const displayedJobs = useMemo(() => {
    const all = Array.isArray(jobs) ? jobs : [];
    const active = all.filter((j) => !j?.claimed);
    if (active.length > 0) return active;

    const claimed = all.filter((j) => !!j?.claimed);
    claimed.sort((a, b) => {
      const aT = a?.claimed_at ? Date.parse(a.claimed_at) : NaN;
      const bT = b?.claimed_at ? Date.parse(b.claimed_at) : NaN;
      if (Number.isFinite(aT) && Number.isFinite(bT) && aT !== bT) return bT - aT;
      const aR = a?.ready_at ? Date.parse(a.ready_at) : NaN;
      const bR = b?.ready_at ? Date.parse(b.ready_at) : NaN;
      if (Number.isFinite(aR) && Number.isFinite(bR) && aR !== bR) return bR - aR;
      return Number(b?.id || 0) - Number(a?.id || 0);
    });
    return claimed.slice(0, 2);
  }, [jobs]);

  const matchedRecipe = useMemo(() => {
    if (selectedSlots.length !== 3) return null;

    // match recipe by exact multiset of item_ids (amounts fixed at 20 each in backend recipes)
    const chosen = [...selectedSlots.map((b) => Number(b.item_id))].sort((a, b) => a - b);

    for (const r of recipes || []) {
      const inputs = r.inputs || [];
      if (inputs.length !== 3) continue;
      const required = inputs.map((i) => Number(i.item_id)).sort((a, b) => a - b);
      const same = required.length === chosen.length && required.every((v, idx) => v === chosen[idx]);
      if (same) return r;
    }

    return null;
  }, [slot1, slot2, slot3, recipes]);

  const requiredCounts = useMemo(() => {
    const map = {};
    const r = matchedRecipe;
    if (!r) return map;
    (r.inputs || []).forEach((ing) => {
      map[Number(ing.item_id)] = (map[Number(ing.item_id)] || 0) + Number(ing.amount || 0);
    });
    return map;
  }, [matchedRecipe]);

  const canStart = useMemo(() => {
    if (!matchedRecipe) return false;

    const requiredLevel = Number(matchedRecipe.required_farming_level || 0);
    if (requiredLevel > 0 && farmingLevel < requiredLevel) return false;

    const needed = {};
    (matchedRecipe.inputs || []).forEach((ing) => {
      needed[Number(ing.item_id)] = (needed[Number(ing.item_id)] || 0) + Number(ing.amount || 0);
    });

    // user picked berries as "one per slot", but recipe cost is 20 each.
    // enforce that user has enough of each ingredient.
    for (const [itemIdStr, amtNeeded] of Object.entries(needed)) {
      const itemId = Number(itemIdStr);
      const meta = (inventoryBerries || []).find((b) => Number(b.item_id) === itemId);
      const have = Number(meta?.owned_amount || 0);
      if (have < Number(amtNeeded)) return false;
    }

    return true;
  }, [matchedRecipe, inventoryBerries, farmingLevel]);

  const missingReason = useMemo(() => {
    if (!matchedRecipe) return "";

    const requiredLevel = Number(matchedRecipe.required_farming_level || 0);
    if (requiredLevel > 0 && farmingLevel < requiredLevel) {
      return `Requires Farming level ${requiredLevel}.`;
    }

    const needed = {};
    (matchedRecipe.inputs || []).forEach((ing) => {
      needed[Number(ing.item_id)] = (needed[Number(ing.item_id)] || 0) + Number(ing.amount || 0);
    });

    for (const [itemIdStr, amtNeeded] of Object.entries(needed)) {
      const itemId = Number(itemIdStr);
      const meta = (inventoryBerries || []).find((b) => Number(b.item_id) === itemId);
      const have = Number(meta?.owned_amount || 0);
      if (have < Number(amtNeeded)) {
        const name = meta?.name || `Item ${itemId}`;
        return `Not enough ${name} (need ${amtNeeded}, have ${have}).`;
      }
    }

    return "";
  }, [matchedRecipe, inventoryBerries, farmingLevel]);

  const clearSlots = () => {
    setSlot1(null);
    setSlot2(null);
    setSlot3(null);
  };

  const placeInFirstEmptySlot = (berry) => {
    if (!slot1) return setSlot1(berry);
    if (!slot2) return setSlot2(berry);
    if (!slot3) return setSlot3(berry);
  };

  const canSelectBerry = (berry) => {
    const itemId = Number(berry?.item_id);
    if (!itemId) return false;

    const owned = Number(inventoryById?.[itemId]?.owned_amount || 0);
    if (owned <= 0) return false;

    // If we already have a matched recipe, prevent selecting berries that would exceed owned quantity
    // relative to what the recipe requires.
    if (matchedRecipe) {
      const req = Number(requiredCounts?.[itemId] || 0);
      if (req > 0) {
        const alreadyChosen = Number(selectedCounts?.[itemId] || 0);
        const unitCost = Number((matchedRecipe.inputs || []).find((x) => Number(x.item_id) === itemId)?.amount || 0);
        const nextNeeded = (alreadyChosen + 1) * unitCost;
        if (nextNeeded > owned) return false;
        if (nextNeeded > req) return false;
      }
    }

    return true;
  };

  const mintItemIds = new Set([
    282, 283, 284, 285, 286, 287, 288, 289, 290, 291, 292, 293, 294, 295,
    296, 297, 298, 299, 300, 301, 302,
  ]);

  const handleStart = async () => {
    if (!matchedRecipe) return;
    if (!stationId) {
      setError("No fusion station selected.");
      return;
    }
    try {
      setBusyId("start");
      const res = await api.post(
        "/v1/island/berry-fusion/start",
        { recipe_key: matchedRecipe.key, station_id: stationId },
        { withCredentials: true }
      );
      if (res?.data?.lucky) {
        const outputId = Number(res?.data?.output_item_id);
        if (outputId === 198) {
          setNotice("Lucky! Your PP Up fusion became a PP Max.");
        } else if (mintItemIds.has(outputId)) {
          setNotice("Lucky! Your candy fusion became a random mint.");
        } else {
          setNotice("Lucky! Your fusion produced a rare result.");
        }
      } else {
        setNotice(null);
      }
      clearSlots();
      await load();
      if (refreshEquippedItems) await refreshEquippedItems();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to start fusion.");
    } finally {
      setBusyId(null);
    }
  };

  const handleClaim = async (jobId) => {
    try {
      setBusyId(jobId);
      const res = await api.post(
        "/v1/island/berry-fusion/claim",
        { job_id: jobId },
        { withCredentials: true }
      );
      if (res?.data?.lucky) {
        const outputId = Number(res?.data?.output_item_id);
        if (outputId === 198) {
          setNotice("Lucky! You claimed a PP Max.");
        } else if (mintItemIds.has(outputId)) {
          setNotice("Lucky! You claimed a random mint.");
        } else {
          setNotice("Lucky! You claimed a rare result.");
        }
      } else {
        setNotice(null);
      }
      await load();
      if (refreshEquippedItems) await refreshEquippedItems();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to claim fusion.");
    } finally {
      setBusyId(null);
    }
  };

  const itemName = (itemId) => {
    const meta = itemMeta?.[Number(itemId)] || {};
    return meta?.name || `Item ${itemId}`;
  };

  const itemImage = (itemId) => {
    const meta = itemMeta?.[Number(itemId)] || {};
    return meta?.image ? `/${meta.image}` : "/items/pokeball.png";
  };

  const formatRemaining = (seconds) => {
    const s = Number(seconds);
    if (!Number.isFinite(s)) return "—";
    if (s <= 0) return "0m";
    if (s >= 86400) return `${Math.ceil(s / 86400)}d`;
    if (s >= 3600) return `${Math.ceil(s / 3600)}h`;
    return `${Math.ceil(s / 60)}m`;
  };

  const renderIcon = (src, alt, size = 20) => (
    <img
      src={src}
      alt={alt}
      style={{ width: size, height: size, flexShrink: 0 }}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = "/items/pokeball.png";
      }}
    />
  );

  const renderSlot = (slot, setSlot, idx) => (
    <button
      onClick={() => setSlot(null)}
      style={{
        width: 80,
        height: 80,
        borderRadius: 14,
        background: "rgba(0,0,0,0.28)",
        border: "1px solid rgba(255,255,255,0.12)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        color: "#fff",
        cursor: slot ? "pointer" : "default",
      }}
      disabled={!slot}
      title={slot ? "Click to clear slot" : "Empty"}
    >
      {slot ? (
        <>
          {renderIcon(slot.image ? `/${slot.image}` : "/items/pokeball.png", slot.name || "Berry", 30)}
          <div style={{ fontSize: 11, fontWeight: 800, opacity: 0.95, textAlign: "center", padding: "0 6px" }}>
            {slot.name || `Berry ${slot.item_id}`}
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, opacity: 0.9, fontWeight: 800 }}>Slot {idx}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Empty</div>
        </>
      )}
    </button>
  );

  return (
    <div
      style={{
        width: layout.panelWidth,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: layout.panelMaxHeight,
        background: "rgba(25, 25, 35, 0.95)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        borderRadius: 12,
        overflow: "hidden",
        color: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          background: "rgba(0,0,0,0.25)",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1 }}>Berry Fusion</div>
          <div style={{ opacity: 0.85, fontSize: 12, marginTop: 4 }}>
            Select 3 berries to discover a recipe.
          </div>
          <div style={{ opacity: 0.75, fontSize: 11, marginTop: 4 }}>
            Farming Level: {farmingLevel}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#fff",
            borderRadius: 8,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          padding: 14,
          overflowY: "auto",
          maxHeight: layout.panelMaxHeight - 60,
        }}
      >
        {notice && (
          <div
            style={{
              marginBottom: 10,
              padding: "10px 12px",
              borderRadius: 10,
              background: "rgba(250,204,21,0.18)",
              border: "1px solid rgba(250,204,21,0.35)",
              fontSize: 12,
              fontWeight: 800,
              color: "#fde68a",
            }}
          >
            {notice}
          </div>
        )}
        {error && (
          <div
            style={{
              marginBottom: 10,
              padding: "10px 12px",
              borderRadius: 10,
              background: "rgba(248,113,113,0.18)",
              border: "1px solid rgba(248,113,113,0.35)",
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ opacity: 0.9, fontSize: 13 }}>Loading...</div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => setActiveTab("craft")}
                style={{
                  background: activeTab === "craft" ? "rgba(255,255,255,0.10)" : "transparent",
                  border: "1px solid rgba(255,255,255,0.14)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontWeight: 800,
                }}
              >
                Craft
              </button>
              <button
                onClick={() => setActiveTab("recipes")}
                style={{
                  background: activeTab === "recipes" ? "rgba(255,255,255,0.10)" : "transparent",
                  border: "1px solid rgba(255,255,255,0.14)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontWeight: 800,
                }}
              >
                Recipes
              </button>
            </div>

            {activeTab === "craft" && (
              <>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Your 3 Slots</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
              {renderSlot(slot1, setSlot1, 1)}
              {renderSlot(slot2, setSlot2, 2)}
              {renderSlot(slot3, setSlot3, 3)}
              <button
                onClick={clearSlots}
                disabled={!slot1 && !slot2 && !slot3}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderRadius: 12,
                  padding: "10px 12px",
                  cursor: slot1 || slot2 || slot3 ? "pointer" : "not-allowed",
                  height: 80,
                  alignSelf: "stretch",
                }}
              >
                Clear
              </button>
            </div>

            <div
              style={{
                padding: 12,
                borderRadius: 12,
                background: "rgba(0,0,0,0.28)",
                border: "1px solid rgba(255,255,255,0.10)",
                marginBottom: 14,
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Output</div>
              {!matchedRecipe ? (
                <div style={{ opacity: 0.85, fontSize: 12 }}>
                  {selectedSlots.length < 3
                    ? "Pick 3 berries to see the resulting item."
                    : "No matching recipe for that combination."}
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {(() => {
                      const outId = Number(matchedRecipe.output_item_id);
                      const meta = itemMeta?.[outId] || {};
                      const img = meta?.image ? `/${meta.image}` : "/items/pokeball.png";
                      const nm = meta?.name || matchedRecipe.name || "Output";
                      return renderIcon(img, nm, 34);
                    })()}
                    <div>
                      <div style={{ fontWeight: 800 }}>
                        {(() => {
                          const outId = Number(matchedRecipe.output_item_id);
                          const meta = itemMeta?.[outId] || {};
                          return meta?.name || matchedRecipe.name;
                        })()}
                      </div>
                      <div style={{ opacity: 0.85, fontSize: 12 }}>
                        {(() => {
                          const sec = Number(matchedRecipe.duration_seconds || 0) || 0;
                          if (sec >= 86400) return `Time: ${Math.round(sec / 86400)} day(s)`;
                          if (sec >= 3600) return `Time: ${Math.round(sec / 3600)} hour(s)`;
                          return `Time: ${Math.max(1, Math.round(sec / 60))} minute(s)`;
                        })()}
                      </div>
                      {Number(matchedRecipe.required_farming_level || 0) > 0 && (
                        <div style={{ opacity: 0.85, fontSize: 12 }}>
                          Requires Farming Level {matchedRecipe.required_farming_level}
                        </div>
                      )}
                      {matchedRecipe.category && (
                        <div style={{ opacity: 0.85, fontSize: 12 }}>
                          Category: {matchedRecipe.category}
                        </div>
                      )}
                      {!!missingReason && (
                        <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6, color: "#fca5a5" }}>
                          {missingReason}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleStart}
                    disabled={!canStart || busyId === "start"}
                    style={{
                      background: canStart ? "rgba(59,130,246,0.85)" : "rgba(255,255,255,0.10)",
                      border: "1px solid rgba(255,255,255,0.14)",
                      color: "#fff",
                      borderRadius: 10,
                      padding: "10px 12px",
                      cursor: canStart ? "pointer" : "not-allowed",
                      minWidth: 120,
                      fontWeight: 800,
                    }}
                  >
                    Start
                  </button>
                </div>
              )}
            </div>

            <div style={{ fontWeight: 800, marginBottom: 8 }}>Pick Berries</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
              {(inventoryBerries || []).map((b) => {
                const owned = Number(b.owned_amount || 0);
                const disabled = !canSelectBerry(b) || (slot1 && slot2 && slot3);
                const selectedCount = Number(selectedCounts?.[Number(b.item_id)] || 0);
                const required = Number(requiredCounts?.[Number(b.item_id)] || 0);
                return (
                  <button
                    key={b.item_id}
                    onClick={() => placeInFirstEmptySlot(b)}
                    disabled={disabled}
                    style={{
                      width: 140,
                      padding: 10,
                      borderRadius: 12,
                      background: "rgba(0,0,0,0.28)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      color: "#fff",
                      cursor: disabled ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                    title={disabled ? "Select slot first" : "Click to add to next empty slot"}
                  >
                    {renderIcon(b.image ? `/${b.image}` : "/items/pokeball.png", b.name || "Berry", 26)}
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontWeight: 800, fontSize: 12, lineHeight: 1.2 }}>{b.name || `Berry ${b.item_id}`}</div>
                      <div style={{ opacity: 0.85, fontSize: 12 }}>Owned: {owned}</div>
                      {matchedRecipe && required > 0 && (
                        <div style={{ opacity: 0.85, fontSize: 12 }}>
                          Required: {required} | Chosen: {selectedCount}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div style={{ fontWeight: 800, marginBottom: 8 }}>Your Fusions</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(displayedJobs || []).length === 0 ? (
                <div style={{ opacity: 0.85, fontSize: 12 }}>No fusions yet.</div>
              ) : (
                (displayedJobs || []).map((j) => {
                  const isReady = !!j.is_ready;
                  const claimed = !!j.claimed;
                  const remainingLabel = formatRemaining(j.remaining_seconds);
                  const lucky = !!j.lucky;
                  const outId = Number(j.output_item_id);
                  const outName = itemName(outId);
                  const outImg = itemImage(outId);

                  return (
                    <div
                      key={j.id}
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        background: "rgba(0,0,0,0.28)",
                        border: "1px solid rgba(255,255,255,0.10)",
                        display: "flex",
                        gap: 12,
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                          <img
                            src={outImg}
                            alt={outName}
                            style={{
                              width: 38,
                              height: 38,
                              imageRendering: "pixelated",
                              filter: !isReady && !claimed ? "grayscale(100%)" : "none",
                              opacity: !isReady && !claimed ? 0.55 : 1,
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/items/pokeball.png";
                            }}
                          />
                          <div style={{ fontSize: 11, opacity: 0.9 }}>
                            {claimed ? "Claimed" : isReady ? "Ready" : remainingLabel}
                          </div>
                        </div>

                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <div style={{ fontWeight: 800, fontSize: 13, minWidth: 0 }}>
                              {outName} ×{Number(j.output_amount || 1)}
                            </div>
                            {lucky && (
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 900,
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  border: "1px solid rgba(250,204,21,0.35)",
                                  background: "rgba(250,204,21,0.16)",
                                  color: "#fde68a",
                                }}
                              >
                                Lucky!
                              </div>
                            )}
                          </div>

                          <div style={{ opacity: 0.85, fontSize: 12, marginTop: 6 }}>
                            Fusing:
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                            {(j.inputs || []).map((ing, idx) => {
                              const ingId = Number(ing.item_id);
                              const nm = itemName(ingId);
                              const img = itemImage(ingId);
                              return (
                                <div
                                  key={`${j.id}-ing-${idx}-${ingId}`}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "4px 8px",
                                    borderRadius: 999,
                                    background: "rgba(255,255,255,0.06)",
                                    border: "1px solid rgba(255,255,255,0.10)",
                                    fontSize: 12,
                                    opacity: 0.95,
                                  }}
                                  title={nm}
                                >
                                  {renderIcon(img, nm, 16)}
                                  <span style={{ fontWeight: 800 }}>×{Number(ing.amount || 0)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {claimed ? (
                        <div
                          style={{
                            minWidth: 92,
                            textAlign: "center",
                            padding: "8px 12px",
                            borderRadius: 10,
                            background: "rgba(255,255,255,0.08)",
                            border: "1px solid rgba(255,255,255,0.12)",
                            color: "rgba(255,255,255,0.85)",
                            fontWeight: 900,
                            fontSize: 12,
                          }}
                        >
                          Claimed
                        </div>
                      ) : (
                        <button
                          onClick={() => handleClaim(j.id)}
                          disabled={!isReady || busyId === j.id}
                          style={{
                            background: isReady ? "rgba(34,197,94,0.80)" : "rgba(255,255,255,0.10)",
                            border: "1px solid rgba(255,255,255,0.14)",
                            color: "#fff",
                            borderRadius: 10,
                            padding: "8px 12px",
                            cursor: isReady ? "pointer" : "not-allowed",
                            minWidth: 92,
                            fontWeight: 800,
                          }}
                        >
                          Claim
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
              </>
            )}

            {activeTab === "recipes" && (
              <>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Recipes</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {Object.entries(
                    (recipes || []).reduce((acc, r) => {
                      const key = r.category || "Other";
                      acc[key] = acc[key] || [];
                      acc[key].push(r);
                      return acc;
                    }, {})
                  ).map(([category, items]) => (
                    <div key={category}>
                      <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8, opacity: 0.9 }}>
                        {category}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {(items || []).map((r) => {
                    const outId = Number(r.output_item_id);
                    const outMeta = itemMeta?.[outId] || {};
                    const outName = outMeta?.name || r.name || `Item ${outId}`;
                    const outImg = outMeta?.image ? `/${outMeta.image}` : "/items/pokeball.png";
                    const sec = Number(r.duration_seconds || 0) || 0;
                    const timeLabel = sec >= 86400
                      ? `${Math.round(sec / 86400)} day(s)`
                      : sec >= 3600
                        ? `${Math.round(sec / 3600)} hour(s)`
                        : `${Math.max(1, Math.round(sec / 60))} minute(s)`;

                    return (
                      <div
                        key={r.key}
                        style={{
                          padding: 12,
                          borderRadius: 12,
                          background: "rgba(0,0,0,0.28)",
                          border: "1px solid rgba(255,255,255,0.10)",
                          display: "flex",
                          gap: 12,
                          alignItems: "center",
                        }}
                      >
                        {renderIcon(outImg, outName, 34)}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 13 }}>{outName}</div>
                          <div style={{ opacity: 0.85, fontSize: 12, marginTop: 4 }}>Time: {timeLabel}</div>
                          {Number(r.required_farming_level || 0) > 0 && (
                            <div style={{ opacity: 0.85, fontSize: 12, marginTop: 4 }}>
                              Requires Farming Level {r.required_farming_level}
                            </div>
                          )}
                          <div style={{ opacity: 0.9, fontSize: 12, marginTop: 8 }}>
                            <span style={{ fontWeight: 800 }}>Inputs:</span>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                            {(r.inputs || []).map((ing, idx) => {
                              const ingId = Number(ing.item_id);
                              const ingMeta = itemMeta?.[ingId] || {};
                              const ingName = ingMeta?.name || inventoryById?.[ingId]?.name || `Item ${ingId}`;
                              const ingImg = ingMeta?.image ? `/${ingMeta.image}` : "/items/pokeball.png";
                              const have = Number(inventoryById?.[ingId]?.owned_amount || 0);
                              const need = Number(ing.amount || 0);
                              const ok = have >= need;
                              return (
                                <div
                                  key={`${r.key}-${ingId}-${idx}`}
                                  style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.95, gap: 10, alignItems: "center" }}
                                >
                                  <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                    {renderIcon(ingImg, ingName, 18)}
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ingName}</span>
                                  </span>
                                  <span style={{ color: ok ? "#86efac" : "#fca5a5", fontWeight: 800 }}>
                                    {have}/{need}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
